import { Controller } from '@hotwired/stimulus';

/**
 * Unified media gallery: one large "stage" showing the active media item and a thumbnail carousel
 * below it. Clicking a thumbnail swaps the stage to that item. Video items keep the standard
 * `video` controller (play in place); photo items open a full-resolution lightbox via the `modal`
 * controller. Video (if present) is index 0 and is shown by default.
 */
export default class extends Controller {
	static targets = ['stage', 'stageItem', 'thumb'];

	connect() {
		this.currentIndex = 0;
		this.activate(0, true);
	}

	select(event) {
		const index = Number(event.params.index);
		if (Number.isNaN(index) || index === this.currentIndex) return;
		this.activate(index, false);
	}

	activate(index, initial) {
		if (!this.hasStageItemTarget || index < 0 || index >= this.stageItemTargets.length) return;

		// Stop whatever was playing in the item we're leaving.
		if (!initial) {
			const prev = this.stageItemTargets[this.currentIndex];
			if (prev) this.stopMedia(prev);
		}

		this.stageItemTargets.forEach((item, i) => {
			item.hidden = i !== index;
		});

		this.thumbTargets.forEach((thumb, i) => {
			thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
		});

		this.currentIndex = index;
		this.scrollThumbIntoView(index);
	}

	/** Pause native video and tear down an activated lite-youtube so audio/playback stops. */
	stopMedia(item) {
		const video = item.querySelector('video');
		if (video) {
			try {
				video.pause();
			} catch (_) {
				/* noop */
			}
		}

		const lite = item.querySelector('lite-youtube, lite-vimeo');
		if (lite) {
			const iframe = lite.querySelector('iframe');
			if (iframe) iframe.remove();
			lite.classList.remove('lyt-activated', 'lvt-activated');
		}

		const player = item.querySelector('[data-controller~="video"]');
		if (player) player.classList.remove('is-playing');
	}

	/** Keep the selected thumbnail visible in the Embla carousel (progressive enhancement). */
	scrollThumbIntoView(index) {
		const emblaEl = this.element.querySelector('[data-controller~="embla"]');
		if (!emblaEl) return;
		const embla = this.application.getControllerForElementAndIdentifier(emblaEl, 'embla');
		embla?.carousel?.scrollTo?.(index);
	}
}
